import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';

export type EstadoChecklistMiaGestor =
  'PENDENTE' | 'EM_ANDAMENTO' | 'PARCIAL' | 'CONCLUIDO' | 'DNS_PENDENTE' | 'DISPONIVEL' | 'ERRO';

export interface ChecklistMiaGestor {
  empresa: EstadoChecklistMiaGestor;
  cardapio: EstadoChecklistMiaGestor;
  formasEntrega: EstadoChecklistMiaGestor;
  link: EstadoChecklistMiaGestor;
}

export interface AcaoPendenteMiaGestor {
  id: number;
  tipo: 'CRIAR_EMPRESA' | 'IMPORTAR_CARDAPIO' | 'CONFIGURAR_FORMAS_ENTREGA';
  risco: 'R2';
  resumo: any;
  impacto: string;
  hashPreview: string;
  expiraEm: string;
}

export interface ProvisionamentoEmpresaMiaGestor {
  id: number;
  status: 'EXECUTANDO' | 'DNS_PENDENTE' | 'CONCLUIDO';
  empresa: {id: number; nome: string; subdominio: string};
  catalogo: {id: number; nome: string};
  responsavel: {id: number; nome: string; email: string | null};
  dns: Array<{
    zona: string;
    hostname: string;
    status: 'PENDENTE' | 'CONFIGURADO' | 'ERRO' | 'NAO_APLICAVEL';
    erro: string | null;
  }>;
  publicacao: 'CONFIGURADA' | 'PENDENTE' | 'NAO_APLICAVEL';
  reutilizado: boolean;
}

export interface AnexoCardapioMiaGestor {
  id: number;
  nome: string;
  mimeType: string;
  tamanhoBytes: number;
  status: string;
  expiraEm: string;
}

export interface AnaliseCardapioMiaGestor {
  id: number;
  /** leitura concluída pelo painel: a Mia já apresentou e puxou o próximo passo */
  turnoMia?: RespostaTurnoMiaGestor | null;
  status: 'ANALISANDO' | 'REVISANDO' | 'AGUARDANDO_APROVACAO' |
    'IMPORTANDO' | 'PARCIAL' | 'CONCLUIDA' | 'FALHOU';
  versao: number;
  hashRascunho: string | null;
  modelo: string | null;
  uso: {tokensEntrada: number; tokensSaida: number; custoDolar: number | null};
  estatisticas: {
    categorias: number;
    produtos: number;
    adicionais: number;
    opcoes: number;
    pendenciasBloqueantes: number;
  } | null;
  /** produtos do rascunho com referencia, nome, preco, tipoProduto, tamanhos e bloqueios */
  categorias: any[];
  /** quantos produtos têm ao menos um bloqueio de importação */
  produtosBloqueados?: number;
  /** tipo de cobrança e tamanhos (nome, pedaços, sabores) quando o rascunho tem pizza */
  configuracaoPizza?: any;
  rascunho?: any;
  resultado: any;
  erro: string | null;
  anexos: AnexoCardapioMiaGestor[];
  itens: Array<{referencia: string; status: string; tentativas: number; erro: string | null}>;
}

export interface SnapshotMiaGestor {
  id?: number;
  onboardingId?: number;
  empresaId: number | null;
  etapa: string;
  status: string;
  versao: number;
  checklist: ChecklistMiaGestor;
  acaoPendente?: AcaoPendenteMiaGestor | null;
  provisionamentoEmpresa?: ProvisionamentoEmpresaMiaGestor | null;
  analiseCardapio?: AnaliseCardapioMiaGestor | null;
}

export interface RespostaRetomadaMiaGestor extends SnapshotMiaGestor {
  conversaId: number | null;
  mensagens: Array<{papel: string; texto: string; criadoEm?: string; modelo?: string | null; botoes?: string[]; lista?: ListaRespostaMiaGestor; link?: LinkRespostaMiaGestor}>;
}

export interface ConviteWhatsappMiaGestor {
  token: string;
  mensagemInicial: string;
  link: string | null;
  expiraEm: string;
  numeroConfigurado: boolean;
  canalAtivo: boolean;
}

export interface ProgressoAnaliseMiaGestor {
  produtos: number;
  categorias: number;
}

/** botão de link (cta_url) que veio na resposta da Mia */
export interface LinkRespostaMiaGestor {
  rotulo: string;
  url: string;
}

export interface ListaRespostaMiaGestor {
  rotulo: string;
  itens: Array<{titulo: string; descricao?: string}>;
}

/** o que o turno custou e quem respondeu — o painel mostra na lateral */
export interface TelemetriaTurnoMiaGestor {
  modelo?: string;
  tokensEntrada?: number;
  tokensSaida?: number;
  tokensCache?: number;
  tokensCacheEscrita?: number;
  custoUsd?: number;
  latenciaMs?: number;
}

export interface RespostaTurnoMiaGestor extends SnapshotMiaGestor {
  texto: string;
  telemetria?: TelemetriaTurnoMiaGestor;
  botoes?: string[];
  lista?: ListaRespostaMiaGestor;
  link?: LinkRespostaMiaGestor;
  conversaId?: number;
  turnoIdGestor?: number | null;
  ferramentas?: Array<{nome: string; sucesso: boolean; duracaoMs: number}>;
}

/** uma mensagem do canal simulado: o que a Mia mandou (ou o operador digitou), já nos limites da Cloud */
export interface MensagemCanalSimuladoMiaGestor {
  id: number;
  papel: 'usuario' | 'gestor' | 'nota';
  texto: string;
  botoes?: Array<{id: string; titulo: string}>;
  lista?: {rotulo: string; itens: Array<{id?: string; titulo: string; descricao?: string}>};
  link?: {url: string; rotulo: string};
  criadoEm: string;
}

/** estado do contato fictício do operador no canal simulado */
export interface EstadoCanalSimuladoMiaGestor {
  waId: string;
  nome: string;
  fase: 'INICIO' | 'SDR' | 'CRIAR_TESTE' | 'AUTENTICADO' | string;
  atendimentoId: number | null;
  modelo: string | null;
  mensagens: MensagemCanalSimuladoMiaGestor[];
  onboarding: SnapshotMiaGestor | null;
}

@Injectable({providedIn: 'root'})
export class MiaGestorService {
  constructor(private http: HttpClient) {}

  obtenhaSaude(): Promise<any> {
    return this.http.get('/gestor-ia/health').toPromise();
  }

  crieOuRetome(): Promise<RespostaRetomadaMiaGestor> {
    return this.http.post<RespostaRetomadaMiaGestor>('/gestor-ia/onboardings', {}).toPromise();
  }

  /** convite do canal WhatsApp: link wa.me com token de uso único (24h) para o número do convidado */
  crieConviteWhatsapp(usuarioId?: number): Promise<ConviteWhatsappMiaGestor> {
    return this.http.post<ConviteWhatsappMiaGestor>('/gestor-ia/convites-whatsapp', usuarioId ? {usuarioId} : {}).toPromise();
  }

  /** encerra a implantação atual e devolve uma nova, do zero */
  reinicie(onboardingId: number): Promise<RespostaRetomadaMiaGestor> {
    return this.http.post<RespostaRetomadaMiaGestor>(
      `/gestor-ia/onboardings/${onboardingId}/reiniciar`, {}
    ).toPromise();
  }

  // ----- simulador como canal (mesmo despachante do WhatsApp) -----

  obtenhaCanalSimulado(): Promise<EstadoCanalSimuladoMiaGestor> {
    return this.http.get<EstadoCanalSimuladoMiaGestor>('/gestor-ia/simulador').toPromise();
  }

  /** texto digitado ou reply button tocado; `travado` segura o que executa de verdade */
  envieNoCanalSimulado(entrada: {texto?: string; botao?: {id: string; titulo: string}}, travado: boolean): Promise<EstadoCanalSimuladoMiaGestor> {
    return this.http.post<EstadoCanalSimuladoMiaGestor>('/gestor-ia/simulador/mensagens', {...entrada, travado}).toPromise();
  }

  /** troca o contato fictício: a próxima mensagem chega como um desconhecido */
  reinicieCanalSimulado(): Promise<EstadoCanalSimuladoMiaGestor> {
    return this.http.post<EstadoCanalSimuladoMiaGestor>('/gestor-ia/simulador/reiniciar', {}).toPromise();
  }

  obtenhaOnboarding(id: number): Promise<RespostaRetomadaMiaGestor> {
    return this.http.get<RespostaRetomadaMiaGestor>(`/gestor-ia/onboardings/${id}`).toPromise();
  }

  envieTurno(onboardingId: number, mensagem: string): Promise<RespostaTurnoMiaGestor> {
    return this.http.post<RespostaTurnoMiaGestor>(
      `/gestor-ia/onboardings/${onboardingId}/turnos`,
      {mensagem}
    ).toPromise();
  }

  aproveAcao(acao: AcaoPendenteMiaGestor, versaoOnboarding: number): Promise<any> {
    return this.http.post(`/gestor-ia/acoes/${acao.id}/aprovar`, {
      hashPreview: acao.hashPreview,
      versaoOnboarding,
    }).toPromise();
  }

  canceleAcao(acaoId: number): Promise<any> {
    return this.http.post(`/gestor-ia/acoes/${acaoId}/cancelar`, {}).toPromise();
  }

  envieAnexosCardapio(onboardingId: number, arquivos: File[]): Promise<{anexos: AnexoCardapioMiaGestor[]}> {
    const formulario = new FormData();
    arquivos.forEach(arquivo => formulario.append('file', arquivo, arquivo.name));
    return this.http.post<{anexos: AnexoCardapioMiaGestor[]}>(
      `/gestor-ia/onboardings/${onboardingId}/anexos`,
      formulario
    ).toPromise();
  }

  analiseCardapio(onboardingId: number, anexoIds: number[]): Promise<AnaliseCardapioMiaGestor> {
    return this.http.post<AnaliseCardapioMiaGestor>(
      `/gestor-ia/onboardings/${onboardingId}/analises`,
      {anexoIds}
    ).toPromise();
  }

  obtenhaProgressoAnalise(onboardingId: number): Promise<{progresso: ProgressoAnaliseMiaGestor | null}> {
    return this.http.get<{progresso: ProgressoAnaliseMiaGestor | null}>(
      `/gestor-ia/onboardings/${onboardingId}/analises/progresso`
    ).toPromise();
  }

  salveRascunhoCardapio(
    onboardingId: number,
    analise: AnaliseCardapioMiaGestor
  ): Promise<AnaliseCardapioMiaGestor> {
    return this.http.put<AnaliseCardapioMiaGestor>(
      `/gestor-ia/onboardings/${onboardingId}/analises/${analise.id}/rascunho`,
      {versao: analise.versao, rascunho: analise.rascunho}
    ).toPromise();
  }

  proponhaImportacaoCardapio(
    onboardingId: number,
    analiseId: number,
    referencias: string[]
  ): Promise<any> {
    return this.http.post(
      `/gestor-ia/onboardings/${onboardingId}/analises/${analiseId}/propor-importacao`,
      {referencias}
    ).toPromise();
  }
}
